// SPDX-License-Identifier: Elastic-2.0
/**
 * Refresh orchestrator (T-007).
 *
 * Schedules a single `setTimeout` to fire 60 s before the active JWT's
 * `exp`, calls `POST /auth/refresh`, and writes the rotated tokens back
 * into `useAuth`. Re-arms on every JWT change via a Zustand subscription.
 *
 * Constraints (per F-310 functional spec § 8 and technical spec § 6.4):
 *
 *   - Cloud sessions only. Local-only mode has no refresh endpoint —
 *     the F-300 `LocalTokenIssuer` issues a long-lived (1y) JWT and
 *     the user re-authenticates via Path B when it expires.
 *
 *   - On refresh success: rotate `jwt` + `refresh` in the store
 *     (which writes through to localStorage). The subscriber re-fires
 *     and schedules the next refresh.
 *
 *   - On refresh failure (401 / network / refresh-token reused): wipe
 *     the cloud session and emit `auth:reauth-required`. A router-level
 *     handler (T-026 / future) navigates to
 *     `/onboarding/account?reauth=true`.
 *
 *   - Tokens whose `exp` is already within the 60-s lead window get an
 *     immediate refresh attempt (delay clamped to ≥ 0 ms). Tokens that
 *     are already expired by the time we reach this function are still
 *     handed to `/auth/refresh` — the refresh chain may still be valid
 *     even if the access token isn't.
 *
 *   - Tokens with no decodable `exp` are skipped (no timer armed); the
 *     next API call will surface the underlying 401 through
 *     `handleApiError()`.
 */

import { AuthService } from "../../api/accounts";
import { eventBus } from "../../api/eventBus";
import { useAuth } from "../../api/auth-store";

/**
 * Refresh fires 60 s before `exp` so a slow network round-trip still
 * has headroom before the access token becomes invalid. Tunable here
 * if we ever switch to longer-lived access tokens.
 */
export const REFRESH_LEAD_TIME_MS = 60 * 1000;

let scheduledTimer: ReturnType<typeof setTimeout> | null = null;
let unsubscribe: (() => void) | null = null;
let inflight: Promise<void> | null = null;

function cancelTimer(): void {
  if (scheduledTimer !== null) {
    clearTimeout(scheduledTimer);
    scheduledTimer = null;
  }
}

/**
 * Attempt to rotate the cloud token chain exactly once. Concurrent
 * callers share the same in-flight promise so a manual refresh call
 * during a scheduled fire doesn't double-spend the refresh token
 * (which would trip F-200's reuse detection and invalidate the chain).
 */
export function refreshNow(): Promise<void> {
  if (inflight) {
    return inflight;
  }
  const task = (async () => {
    const state = useAuth.getState();
    const refresh = state.refresh;
    if (!refresh) {
      // No refresh token to spend — treat as terminal failure.
      state.clear();
      eventBus.emit("auth:reauth-required");
      return;
    }
    try {
      const response = await AuthService.refreshAuthRefreshPost({
        requestBody: { refresh_token: refresh },
      });
      // Re-read state in case the user hit "sign out" mid-flight.
      const after = useAuth.getState();
      if (!after.refresh) {
        // User cleared the session while we were on the wire — drop
        // the rotated tokens on the floor rather than resurrecting it.
        return;
      }
      // Keep the existing user/tier — `/auth/refresh` doesn't return
      // them. Onboarding / shell call `/me` separately when they need
      // a fresh tier (e.g. after a Stripe webhook in Phase 2).
      after.setCloudSession(
        response.access_token,
        response.refresh_token,
        after.user ?? { id: "", email: "", display_name: "" },
        after.tier ?? "free",
      );
    } catch {
      // 401 / 5xx / network — single attempt, no retry. F-200's
      // reuse-detection means a second presentation of the same
      // refresh token is fatal anyway. Wipe + signal re-auth.
      useAuth.getState().clear();
      eventBus.emit("auth:reauth-required");
    }
  })();
  inflight = task.finally(() => {
    inflight = null;
  });
  return inflight;
}

/**
 * Recompute when the next refresh should fire and (re-)arm the timer.
 * Called on every JWT change.
 */
function reschedule(): void {
  cancelTimer();

  const state = useAuth.getState();
  // Refresh is only meaningful in cloud mode. Local-only JWT lives a
  // year; unauthenticated has no token at all.
  if (state.mode !== "cloud" || !state.jwt) return;

  const expSec = state.getTokenExpiry();
  if (expSec === null) {
    // Malformed token — bail out cleanly; the next request will
    // surface the underlying 401 through `handleApiError()`.
    return;
  }

  const nowMs = Date.now();
  const fireAtMs = expSec * 1000 - REFRESH_LEAD_TIME_MS;
  // `setTimeout` stores its delay in a signed 32-bit int internally
  // (2^31 - 1 ms ≈ 24.8 days). Pass anything larger and the value
  // silently overflows / is clamped to ~0, so the timer fires
  // IMMEDIATELY — exactly the opposite of what a far-future exp asks
  // for. We clamp at 23 days (1.99 billion ms) to stay comfortably
  // inside the int32 ceiling. The orchestrator re-arms on every JWT
  // change anyway, so capping the timer just means we re-evaluate
  // every 23 days for an absurdly long-lived token rather than
  // burning through a refresh dispatch immediately on boot.
  const MAX_TIMER_DELAY_MS = 23 * 24 * 60 * 60 * 1000; // 23 days
  const delayMs = Math.min(MAX_TIMER_DELAY_MS, Math.max(0, fireAtMs - nowMs));

  scheduledTimer = setTimeout(() => {
    void refreshNow();
  }, delayMs);
}

/**
 * Subscribe to the store and arm the first refresh timer. Returns the
 * teardown function — tests call it between cases; production code
 * never tears down.
 *
 * Idempotent: calling twice cancels the previous subscription before
 * re-arming, so hot-reload doesn't stack timers.
 */
export function startRefreshOrchestrator(): () => void {
  // Tear down any previous arming first.
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
  cancelTimer();

  // Re-arm whenever the active cloud JWT changes (login, refresh,
  // logout). We also watch `mode` so the local-only → cloud transition
  // arms a timer that wasn't there before.
  unsubscribe = useAuth.subscribe((state, prev) => {
    if (state.jwt !== prev.jwt || state.mode !== prev.mode) {
      reschedule();
    }
  });

  // Arm immediately for whatever's already in state.
  reschedule();

  return () => {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
    cancelTimer();
  };
}

/**
 * Test-only — exposes the internal state so tests can assert that
 * a timer is/isn't armed without sleeping for real time.
 */
export const __internal = {
  getScheduledTimer: () => scheduledTimer,
  hasSubscription: () => unsubscribe !== null,
  reschedule,
  cancelTimer,
};
